import classNames from 'classnames';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Connection, ConnectionStop, ConnectionTime, Tag, Time } from '@flixbus/honeycomb-react';

import JobList from 'common/JobList';
import RunItem from 'common/RunItem';
import StatusIcon from 'common/StatusIcon';
import TimezoneTabs from 'common/TimezoneTabs';
import { getTagAppearanceProp } from 'utils/assigmentStatusToTagVariant';
import { formatWeekdayDate, formatTime } from 'utils/datetime';

import { workAssignmentDetailsTooltipText } from './text';

import styles from './styles.module.css';

const getStationKey = (station, index) => station.id ?? `${station.code}-${index}`;

const WorkAssignmentDetailsTooltip = ({
	assignment,
	timezone,
	isShort = false,
	isSegment = false,
}) => {
	const { line, tripNumber, route, crewMember, status, rideId, jobs, runs } = assignment;
	const navigate = useNavigate();

	const renderJobs = () => {
		return (
			<JobList isTag jobs={jobs} extraClasses={styles.bold} extraJobLabelClasses={styles.jobCode} />
		);
	};

	const renderRuns = () => {
		return (
			<>
				{runs && runs.length > 0 ? (
					runs.map((run) => (
						<RunItem
							key={run.id}
							isTag
							run={run}
							extraClasses={styles.bold}
							extraRunLabelClasses={styles.runName}
						/>
					))
				) : (
					<RunItem run={{}} extraClasses={styles.bold} />
				)}
			</>
		);
	};

	const renderHeader = () => {
		if (isShort) return;

		return (
			<div className={styles.header}>
				<div className={classNames(styles.margin, styles.bold, styles.nextline)}>
					{`${route[0].name}  → ${route[route.length - 1].name}`}
				</div>
				<div>
					<span className={styles.bold}>{workAssignmentDetailsTooltipText.line}</span> {line.code}
				</div>
				<div>
					<span className={styles.bold}>{workAssignmentDetailsTooltipText.tripNumber}</span>{' '}
					{tripNumber}
				</div>
				<div className={classNames(styles.margin, styles.nextline)}>
					<span className={styles.bold}>{workAssignmentDetailsTooltipText.uuid}</span>
					{rideId}
				</div>
				<div className={styles.margin}>{renderRuns()}</div>
				<div className={styles.margin}>{renderJobs()}</div>

				<div className={styles.margin}>
					<span className={styles.bold}>{workAssignmentDetailsTooltipText.departure}</span>
					{formatWeekdayDate(route[0].plannedDeparture)}
				</div>

				<Link to={`/details/${rideId}`} className={classNames(styles.link, styles.margin)}>
					<span className={styles.linkLabel}>{workAssignmentDetailsTooltipText.rideDetails}</span>
					<span className={styles.noUnderline}>→</span>
				</Link>
			</div>
		);
	};

	const createTimeProps = (stationConfig) => {
		const { station, timezoneToUse, isFirst } = stationConfig;
		const timeProps = {};
		if (!station.plannedArrival && !station.plannedDeparture) return timeProps;

		// Use passed timezone or fallback to station timezone
		const timezone = timezoneToUse || station.timezone;

		const isStart = !station.plannedArrival || isFirst;

		const firstTime = isStart ? station.plannedDeparture : station.plannedArrival;
		const firstTimeLabel = isStart
			? workAssignmentDetailsTooltipText.departureTime
			: workAssignmentDetailsTooltipText.arrival;
		const secondTime =
			station.plannedDeparture && station.plannedArrival ? station.plannedDeparture : '';

		timeProps.time = (
			<Time
				className={styles.bold}
				label={firstTimeLabel}
				Elem="ins"
				dateTime={formatTime(firstTime, { timezone })}
			>
				{formatTime(firstTime, { timezone })}
			</Time>
		);

		if (secondTime && !isShort) {
			timeProps.secondTime = (
				<Time
					className={styles.bold}
					label={workAssignmentDetailsTooltipText.departureTime}
					Elem="ins"
					dateTime={formatTime(station.plannedDeparture, { timezone })}
				>
					{formatTime(station.plannedDeparture, { timezone })}
				</Time>
			);
		}
		return timeProps;
	};

	const trimRoute = () => {
		if (!isShort) return route;
		const routeLength = route.length;
		switch (routeLength) {
			case 1:
			case 2:
			case 3:
				return route;
			default:
				return [
					route[0],
					{
						code: '',
						name: `${route.length - 2} ${workAssignmentDetailsTooltipText.stations}`,
						arrival: '',
						departure: '',
					},
					route[route.length - 1],
				];
		}
	};

	const renderDriverDetails = () => {
		const isDriverAssigned = Object.hasOwn(crewMember, 'fullName');
		return (
			<>
				<div className={styles.bold}>{workAssignmentDetailsTooltipText.assignedCrewMember}</div>

				<div className={styles.driver}>
					{isDriverAssigned && (
						<span
							className={styles.name}
							onClick={() => navigate(`/crew-management/${crewMember.id}`)}
						>
							{crewMember?.fullName}
						</span>
					)}
					{isDriverAssigned && (
						<Tag small {...getTagAppearanceProp(status)}>
							<StatusIcon status={status} />
							{status}
						</Tag>
					)}
					{!isDriverAssigned && (
						<div className={styles.nodriver}>{workAssignmentDetailsTooltipText.noCrewMember}</div>
					)}
				</div>
			</>
		);
	};

	// Create a wrapper component that receives timezone from TimezoneTabs
	const ConnectionWrapper = ({ selectedTimezone }) => {
		const routeToRender = trimRoute();

		return (
			<Connection appearance="success" extraClasses={styles.fullwidth}>
				{routeToRender.map((station, index) => (
					<ConnectionStop
						key={getStationKey(station, index)}
						station={
							<div className={styles.station}>
								{station.code}({station.name})
							</div>
						}
						time={
							<ConnectionTime
								{...createTimeProps({
									station,
									timezoneToUse: selectedTimezone,
									isFirst: index === 0,
								})}
							/>
						}
					/>
				))}
			</Connection>
		);
	};

	if (!assignment) return null;
	return (
		<div className={styles.assigment}>
			{renderHeader()}
			{renderDriverDetails()}
			{isSegment && (
				<>
					{renderRuns()}
					{renderJobs()}
				</>
			)}
			<div className={styles.bold}>{workAssignmentDetailsTooltipText.route}</div>
			<div className={styles.details}>
				{isSegment ? (
					<ConnectionWrapper selectedTimezone={timezone} />
				) : (
					<TimezoneTabs
						stationTimezone={route[0]?.timezone}
						instanceId={`assignment-${rideId}-${Math.random().toString(36).substr(2, 9)}`}
					>
						<ConnectionWrapper selectedTimezone={route[0]?.timezone} />
					</TimezoneTabs>
				)}
			</div>
		</div>
	);
};

export default WorkAssignmentDetailsTooltip;
